import { useState, useEffect } from 'react';
import {
  Card, CardHeader, CardBody, Button, Field, Input, ErrorNotice, Loading,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.jsx';
import { Building2 } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/**
 * MA-9: "Changes are reflected in a live checkout preview beside the form."
 * A merchant choosing an accent colour is really choosing what their customer
 * sees, so showing them the field and not the result makes them guess.
 */
function CheckoutPreview({ branding, merchantName }) {
  const accent = /^#[0-9a-fA-F]{6}$/.test(branding.accentColor ?? '') ? branding.accentColor : '#4f46e5';
  const displayName = branding.displayName?.trim() || merchantName || 'Your business';

  return (
    <div className="rounded-xl border border-line bg-surface-3 p-4">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
        What your customer sees
      </p>

      <div className="mx-auto max-w-sm overflow-hidden rounded-xl bg-white shadow-md">
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt=""
              className="h-7 w-7 rounded object-contain"
              // A broken logo URL must not leave a broken-image icon on checkout.
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <span
              className="grid h-7 w-7 place-items-center rounded text-xs font-bold text-white"
              style={{ backgroundColor: accent }}
            >
              {displayName.charAt(0).toUpperCase()}
            </span>
          )}
          <span className="truncate text-sm font-semibold text-ink">{displayName}</span>
        </div>

        <div className="px-4 py-4">
          <p className="text-[11px] uppercase tracking-wide text-muted">Total</p>
          <p className="tabular mt-0.5 text-2xl font-semibold text-ink">$24.00</p>

          <div className="mt-4 space-y-2">
            <div className="h-9 rounded-md border border-line bg-surface-2" />
            <div className="grid grid-cols-2 gap-2">
              <div className="h-9 rounded-md border border-line bg-surface-2" />
              <div className="h-9 rounded-md border border-line bg-surface-2" />
            </div>
          </div>

          {/* HC-1: the pay button uses the merchant's accent colour. */}
          <button
            type="button"
            disabled
            className="mt-4 h-10 w-full rounded-md text-sm font-semibold text-white"
            style={{ backgroundColor: accent }}
          >
            Pay $24.00
          </button>

          <p className="mt-3 text-center text-[11px] text-faint">
            {/* HC-14 */}
            Card details go straight to the payment network.
          </p>
          {branding.supportEmail && (
            <p className="mt-1 text-center text-[11px] text-faint">Questions? {branding.supportEmail}</p>
          )}
        </div>
      </div>

      {/* HC-2, restated for the merchant's benefit. */}
      <p className="mt-3 text-center text-[11px] text-muted">
        No processor name or logo appears anywhere on this page.
      </p>
    </div>
  );
}

export function ProfilePage() {
  const toast = useToast();
  const { setMerchant } = useAuth();
  const { data, error, loading, refetch } = useResource('/dashboard/profile');

  const [branding, setBranding] = useState({
    displayName: '', logoUrl: '', accentColor: '#4f46e5', supportEmail: '', statementDescriptor: '',
  });

  useEffect(() => {
    if (data?.branding) setBranding({ ...branding, ...data.branding });
  }, [data]);

  const save = useMutation(async () => {
    const updated = await http.patch('/dashboard/profile', branding);
    setMerchant(updated);
    toast.success('Branding saved', { description: 'Your checkout pages are updated immediately.' });
    refetch();
  });

  if (loading) return <Loading label="Loading your profile…" />;
  if (error) {
    return (
      <>
        <PageHero title="Business profile" />
        <ErrorNotice error={error} onRetry={refetch} />
      </>
    );
  }

  const set = (key) => (e) => setBranding((b) => ({ ...b, [key]: e.target.value }));
  const fieldErrors = save.error?.fieldErrors ?? {};

  return (
    <>
      <PageHero
        icon={Building2}
        chip="Account"
        title="Business profile"
        blurb="How your business appears to customers on the checkout page."
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Card>
          <CardHeader title="Branding" />
          <CardBody>
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate().catch(() => {}); }}>
              {save.error && !Object.keys(fieldErrors).length && <ErrorNotice error={save.error} title="Could not save" />}

              <Field label="Display name" hint="Shown at the top of your checkout page." error={fieldErrors.displayName}>
                {(props) => <Input {...props} value={branding.displayName ?? ''} onChange={set('displayName')} placeholder={data.name} />}
              </Field>

              <Field label="Logo URL" hint="A square image works best. Served over HTTPS." error={fieldErrors.logoUrl}>
                {(props) => (
                  <Input {...props} type="url" inputMode="url" value={branding.logoUrl ?? ''}
                    onChange={set('logoUrl')} placeholder="https://yourstore.com/logo.png" />
                )}
              </Field>

              <Field label="Accent colour" hint="Used for the pay button." error={fieldErrors.accentColor}>
                {(props) => (
                  <div className="flex gap-2">
                    <Input {...props} value={branding.accentColor ?? ''} onChange={set('accentColor')}
                      placeholder="#4f46e5" className="font-mono" />
                    <input
                      type="color"
                      aria-label="Pick an accent colour"
                      value={/^#[0-9a-fA-F]{6}$/.test(branding.accentColor ?? '') ? branding.accentColor : '#4f46e5'}
                      onChange={set('accentColor')}
                      className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-line bg-white p-1"
                    />
                  </div>
                )}
              </Field>

              <Field label="Support email" hint="Shown to customers who need help with a payment." error={fieldErrors.supportEmail}>
                {(props) => <Input {...props} type="email" value={branding.supportEmail ?? ''} onChange={set('supportEmail')} />}
              </Field>

              <Field
                label="Statement descriptor"
                hint="What appears on your customer's bank statement. Most providers allow 22 characters."
                error={fieldErrors.statementDescriptor}
              >
                {(props) => (
                  <Input {...props} maxLength={22} value={branding.statementDescriptor ?? ''}
                    onChange={set('statementDescriptor')} placeholder="BEANTHERE COFFEE" />
                )}
              </Field>

              <div className="border-t border-line pt-4">
                <Button type="submit" loading={save.loading}>Save changes</Button>
              </div>
            </form>
          </CardBody>
        </Card>

        <div className="lg:sticky lg:top-6 lg:self-start">
          <CheckoutPreview branding={branding} merchantName={data.name} />
        </div>
      </div>
    </>
  );
}

export default ProfilePage;
